import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { TokenStorageService } from '../token-storage.service';
import {
  FrontendUserVerificationCodeModel,
  ResponseModel,
  SessionModel,
  UserModel,
} from '@coreModels';
import { ApiConstants } from '@coreConstant';
import { ToastService } from '../toast.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly CLIENT_ID: string = environment.CLIENT_ID;
  private readonly CLIENT_SECRET: string = environment.CLIENT_SECRET;
  private readonly GRANT_TYPE: string = environment.GRANT_TYPE;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private tokenStorageService: TokenStorageService,
    private toastService: ToastService
  ) {}

  login(requestModel: any): Observable<SessionModel> {
    requestModel.scope = '';
    requestModel.client_id = requestModel.client_id || this.CLIENT_ID;
    requestModel.client_secret =
      requestModel.client_secret || this.CLIENT_SECRET;
    requestModel.grant_type = this.GRANT_TYPE;
    return this.http.post(ApiConstants.LOGIN_URL, requestModel);
  }

  getLoginUserDetail(): Observable<any> {
    return this.http.get(ApiConstants.USER_DETAIL_URL).pipe(
      map((response: any) => (response.data || response?.body.data) as any),
      catchError((err: any) => {
        this.toastService.showError(err);
        return throwError(() => err);
      })
    );
  }

  sendVerificationcode(
    data: FrontendUserVerificationCodeModel
  ): Observable<ResponseModel> {
    return this.http.post(ApiConstants.USER_VERIFICATION_CODE_URL, data).pipe(
      map((response: any) => response.data as any),
      catchError((err: any) => {
        this.toastService.showError(err);
        return throwError(() => err);
      })
    );
  }

  reSendVerificationCode(
    data: FrontendUserVerificationCodeModel
  ): Observable<ResponseModel> {
    return this.http
      .post(ApiConstants.USER_RESEND_VERIFICATION_CODE_URL, data)
      .pipe(
        map((response: any) => response.data as any),
        catchError((err: any) => {
          this.toastService.showError(err);
          return throwError(() => err);
        })
      );
  }

  verifiyCode(
    data: FrontendUserVerificationCodeModel
  ): Observable<ResponseModel> {
    return this.http.post(ApiConstants.USER_VERIFY_CODE_URL, data).pipe(
      map((response: any) => response.data as any),
      catchError((err: any) => {
        return throwError(() => err);
      })
    );
  }

  loginWithFacebook(requestModel: any): Observable<SessionModel> {
    return this.http
      .post(ApiConstants.LOGIN_WITH_FACEBOOK_URL, requestModel)
      .pipe(
        map((response: any) => response),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  loginWithGoogle(requestModel: any): Observable<SessionModel> {
    return this.http
      .post(ApiConstants.LOGIN_WITH_GOOGLE_URL, requestModel)
      .pipe(
        map((response: any) => response),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  signUp(requestModel: UserModel): Observable<UserModel> {
    return this.http.post(ApiConstants.REGISTER_URL, requestModel).pipe(
      map((response: any) => response.data),
      catchError((err: any) => {
        return throwError(() => err);
      })
    );
  }

  forgotPassword(forgotPasswordModel: any): Observable<ResponseModel> {
    return this.http
      .post(ApiConstants.USER_FORGOT_PASSWORD_URL, forgotPasswordModel)
      .pipe(
        map((response: any) => response.data),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  changePassword(changePasswordModel: any): Observable<ResponseModel> {
    return this.http
      .post(ApiConstants.USER_CHANGE_PASSWORD_URL, changePasswordModel)
      .pipe(
        map((response: any) => response.data),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  resetPassword(resetPasswordModel: any): Observable<ResponseModel> {
    return this.http
      .post(ApiConstants.USER_RESET_PASSWORD_URL, resetPasswordModel)
      .pipe(
        map((response: any) => response.data),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  isUserAuthenticated(): any {
    if (isPlatformBrowser(this.platformId)) {
      // Now it's safe to access localStorage
      return this.tokenStorageService.getToken() !== null;
    }
  }

  isrCeditEarnDisabled(): any {
    // Now it's safe to access localStorage
    return this.tokenStorageService.getCreditEarn();
  }

  isCreditEarnDisabled(): any {
    if (isPlatformBrowser(this.platformId)) {
      // Now it's safe to access localStorage
      return this.tokenStorageService.getToken() !== null;
    }
  }

  contactUs(body: any): Observable<ResponseModel> {
    return this.http.post('contact-us', body).pipe(
      map((response: any) => response.data),
      catchError((err: any) => {
        return throwError(() => err);
      })
    );
  }

  updateProfile(requestModel: UserModel): Observable<UserModel> {
    return this.http
      .patch(ApiConstants.USER_UPDATE_DETAIL_URL, requestModel)
      .pipe(
        map((response: any) => response.data),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  updateProfilePicture(requestModel: any) {
    return this.http
      .post(ApiConstants.USER_UPDATE_PROFILE_PICTURE_URL, requestModel)
      .pipe(
        map((response: any) => response.data), // Map the API response
        catchError((err: any) => {
          this.toastService.showError(err);
          return throwError(() => err); // Pass the error to the caller
        })
      );
  }
}
